import { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { fromEvent } from 'rxjs';
import { useSelector } from 'react-redux';
import { PaletteMode, useEventCallback } from '@mui/material';

import { preferredThemeModeLocalStorageKey } from '/constants/themeConstants';
import { StorageContext } from '/storage';
import { useAppDispatch } from '/store/hooks';
import { FCWithChildren } from '/common/models';

import type { IUIState } from './toolkits/ui/state';
import type { IAuthState } from './toolkits/auth';
import { baseSelectors as authSelectors } from './toolkits/auth/selectors';
import { logout as logoutAction } from './toolkits/auth/actions';
import { appToolkit } from './toolkits/app/slice';
import { uiToolkit } from './toolkits/ui/slice';
import { activeSpaceToolkit } from './toolkits/space/slice';

export const StorePersist: FCWithChildren = ({ children }) => {
  const storage = useContext(StorageContext);
  const auth = useSelector(authSelectors.state);
  const app = useSelector(appToolkit.selectors.state);
  const space = useSelector(activeSpaceToolkit.selectors.activeSpace);
  const ui = useSelector(uiToolkit.selectors.state);

  const [themeModelessUi, setThemeModelessUi] = useState<IUIState>(ui);
  const [currentUiThemeMode, setCurrentUiThemeMode] = useState<PaletteMode>(ui.currentThemeMode);

  const dispatch = useAppDispatch();
  const logout = useEventCallback(() => dispatch(logoutAction()));
  const handleUiChanges = useCallback(() => {
    const { currentThemeMode } = ui;
    setThemeModelessUi({ ...ui, currentThemeMode });
    setCurrentUiThemeMode(currentThemeMode);
  }, [ui]);

  useEffect(() => {
    // is case user is not loggedIn, logout in focus
    const sub = fromEvent(window, 'focus').subscribe(() => {
      const isLoginPage = window.location.pathname === '/login';
      const isGoogleAuthPage = window.location.pathname === '/auth/google';

      if (isLoginPage || isGoogleAuthPage) {
        return;
      }

      const lAuth = localStorage.getItem('auth');
      const sAuth = sessionStorage.getItem('auth');

      if (sAuth) {
        const { loggedIn } = JSON.parse(sAuth) as IAuthState;
        if (!loggedIn) {
          logout();
          return;
        }

        return;
      }

      if (lAuth) {
        const { loggedIn } = JSON.parse(lAuth) as IAuthState;
        if (!loggedIn) {
          logout();
        }
      }
    });

    return () => sub.unsubscribe();
  }, [logout]);

  useEffect(() => {
    if (auth.rememberMe) {
      localStorage.setItem('auth', JSON.stringify(auth));
      sessionStorage.removeItem('auth');
    } else {
      // set auth to the session store in case user refresh the page
      sessionStorage.setItem('auth', JSON.stringify(auth));
      localStorage.removeItem('auth');
    }
  }, [auth]);

  useEffect(() => {
    if (app.app) {
      localStorage.setItem(`app:${app.app.routeName}`, JSON.stringify(app));
    }
  }, [app]);

  useEffect(handleUiChanges, [handleUiChanges]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (auth.user) {
      localStorage.setItem(`ui:${auth.user.id}`, JSON.stringify(themeModelessUi));
    }
  }, [auth.user, themeModelessUi]);

  useEffect(() => {
    if (currentUiThemeMode) {
      localStorage.setItem(preferredThemeModeLocalStorageKey, currentUiThemeMode);
    }
  }, [currentUiThemeMode]);

  useEffect(() => {
    if (space?.id) {
      storage.set('activeSpaceID', space.id.serialize());
    } else {
      storage.remove('activeSpaceID');
    }
  }, [space, storage]);

  return children as ReactElement;
};
